import React, { useEffect, useMemo, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import JSZip from "jszip";

// Product images (local, bundled by Vite)
import imgBioAge from "../assets/Products/bioage.jpg";
import imgBrainSupportPack from "../assets/Products/brain-support-pack.png";
import imgCollagenPlus from "../assets/Products/collagen-plus.png";
import imgEnergy from "../assets/Products/energy.png";
import imgEssentialsPack from "../assets/Products/essentials-pack.png";
import imgGutSupportPack from "../assets/Products/gut-support-pack.png";
import imgImmuneSupportPack from "../assets/Products/immune-support-pack.png";
import imgLoad from "../assets/Products/load.jpg";
import imgSleep from "../assets/Products/sleep.png";
import imgSlimPlus from "../assets/Products/slim-plus.png";
import imgPrenatalPack from "../assets/Products/prenatal_support.png";
import imgHydrate from "../assets/Products/hydrate.png";
import imgDNA from "../assets/Products/dna.png";
import imgStressSupportPack from "../assets/Products/stress_pack.png";

// Home hero images (light/dark)
import heroLight from "../assets/Products/light-home-image.png";
import heroDark from "../assets/Products/dark-mode-homepg.png";

// IDLife logo
import idlifeLogo from "../assets/Products/idlife-logo.png";

// Hero animation - PNG sequence for all browsers (memory-safe approach)
import animSequenceZip from "../assets/Products/anim.sequence.zip?url";


/**
 * StartSmart Wizard (production-friendly)
 * - 5 questions
 * - Q1 multi-select up to 2
 * - Single best-fit path -> top 3 products
 *
 * Security / robustness:
 * - No dangerouslySetInnerHTML
 * - Only allows selection of known option IDs
 * - Deterministic selection + tie-break
 */

type PathKey =
  | "ENERGY"
  | "SLEEP_STRESS"
  | "METABOLISM_WEIGHT"
  | "LONGEVITY_CELLULAR"
  | "BRAIN_COGNITION"
  | "GUT_DIGESTION"
  | "IMMUNE"
  | "PERFORMANCE_RECOVERY"
  | "GENERAL_FOUNDATION"
  | "PREGNANCY_INFERTILITY";

const PRODUCT_IMAGES: Record<string, string> = {
  "BioAge": imgBioAge,
  "Brain Support Pack": imgBrainSupportPack,
  "Collagen+": imgCollagenPlus,
  "Energy": imgEnergy,
  "Essentials Pack": imgEssentialsPack,
  "Gut Support Pack": imgGutSupportPack,
  "Immune Support Pack": imgImmuneSupportPack,
  "Load": imgLoad,
  "Sleep": imgSleep,
  "Slim+": imgSlimPlus,
  "Prenatal Pack": imgPrenatalPack,
  "Hydrate": imgHydrate,
  "DNA": imgDNA,
  "Stress Support Pack": imgStressSupportPack,
};

type Product = {
  name: string;
  url?: string;
  tagline?: string;
  imageSrc?: string;
};




type SolutionPath = {
  key: PathKey;
  title: string;
  description: string;
  products: [Product, Product, Product];
};

type Option = {
  id: string;
  label: string;
  pointsByPath: Partial<Record<PathKey, number>>;
  sublabel?: string;
};

type Question = {
  id: string;
  text: string;
  helper?: string;
  multiSelect: boolean;
  maxSelect: number;
  options: Option[];
};

type AnswersState = Record<string, string[]>;
type Scores = Record<PathKey, number>;

type StartSmartSessionPayload = {
  ipAddress: string;
  location: string;
  startDate: string;
  dayOfWeek: string;
  sessionLength: string;
  question1_1: string;
  question1_2: string;
  question2_1: string;
  question2_2: string;
  question2_3: string;
  question2_4: string;
  question2_5: string;
  question2_6: string;
  question2_7: string;
  question3: string;
  question4: string;
  question5: string;
  outputCategory: string;
  outputProduct1: string;
  outputProduct2: string;
  outputProduct3: string;
};

type IpLocation = {
  ipAddress: string;
  location: string;
};

type FutureTrackingState = {
  restartCount: number;
  backEvents: Array<{ stage: "start" | "quiz" | "results"; stepIdx: number; at: string }>;
  viewedRecommendedProducts: {
    product1: boolean;
    product2: boolean;
    product3: boolean;
  };
};

const STARTSMART_API_ENDPOINT = "https://startsmartapi.idlife.com/api/sessions";
const STARTSMART_API_KEY = "1f9f1354-e8c6-481d-8c0f-535984429b60";
const TRACKING_QUEUE_STORAGE_KEY = "startsmart-tracking-queue-v1";
const FUTURE_TRACKING_DRAFT_STORAGE_KEY = "startsmart-future-tracking-draft-v1";

const OUTPUT_CATEGORY_MAP: Record<PathKey, string> = {
  ENERGY: "ENERGY",
  SLEEP_STRESS: "SLEEP_STRESS",
  METABOLISM_WEIGHT: "METABOLISM_WEIGHT",
  LONGEVITY_CELLULAR: "LONGEVITY_CELLULAR",
  BRAIN_COGNITION: "BRAIN_COGNITION",
  GUT_DIGESTION: "GUT_DIGESTION",
  IMMUNE: "IMMUNE",
  PERFORMANCE_RECOVERY: "PERFORMANCE_RECOVERY",
  GENERAL_FOUNDATION: "GENERAL_FOUNDATION",
  PREGNANCY_INFERTILITY: "PREGNANCY_NURSING",
};

const QUESTIONS: Question[] = [
  {
    id: "q1",
    text: "What are you hoping supplements can help solve right now?",
    helper: "Select up to two.",
    multiSelect: true,
    maxSelect: 2,
    options: [
      { id: "1", label: "Low energy or fatigue", pointsByPath: { ENERGY: 5 } },
      { id: "2", label: "Weight management or metabolism", pointsByPath: { METABOLISM_WEIGHT: 5 } },
      { id: "3", label: "Poor sleep or stress", pointsByPath: { SLEEP_STRESS: 5 } },
      { id: "4", label: "Brain health, focus, or memory", pointsByPath: { BRAIN_COGNITION: 5 } },
      { id: "5", label: "Aging, longevity, or cellular health", pointsByPath: {} },
      { id: "6", label: "Gut health or digestion", pointsByPath: { GUT_DIGESTION: 5 } },
      { id: "7", label: "Immune support", pointsByPath: { IMMUNE: 5 } },
      { id: "8", label: "Pregnancy, nursing or infertility", pointsByPath: { PREGNANCY_INFERTILITY: 10 } },
      { id: "9", label: "Hormones or mood", pointsByPath: { SLEEP_STRESS: 5 } },
      { id: "10", label: "Athletic performance or recovery", pointsByPath: { PERFORMANCE_RECOVERY: 5 } },
      { id: "11", label: "General health maintenance", pointsByPath: { GENERAL_FOUNDATION: 5 } },
      { id: "12", label: "I'm not sure. I just know something feels off", pointsByPath: { GENERAL_FOUNDATION: 5 } },
    ],
  },
      {
    id: "q2",
    text: "Which issue have you been dealing with most recently?",
    helper: "Check all that apply.",
    multiSelect: true,
    // 1–7 can be combined (up to all 7). Option 8 is exclusive (handled in toggle logic below).
    maxSelect: 7,
    options: [
      { id: "1", label: "Exhausted even after sleep", pointsByPath: { ENERGY: 5 } },
      { id: "2", label: "Stressed / wired / not sleeping well", pointsByPath: { SLEEP_STRESS: 5 } },
      { id: "3", label: "Stuck with weight or cravings", pointsByPath: { METABOLISM_WEIGHT: 5 } },
      { id: "4", label: "Inflamed / sore / slow recovery", pointsByPath: { PERFORMANCE_RECOVERY: 5 } },
      { id: "5", label: "Digestion issues / bloating", pointsByPath: { GUT_DIGESTION: 5 } },
      { id: "6", label: "Want stronger immune defense", pointsByPath: { IMMUNE: 5 } },
      { id: "7", label: "Focus / clarity isn’t what it used to be", pointsByPath: { BRAIN_COGNITION: 5 } },
      { id: "8", label: "None — I want a solid foundation", pointsByPath: { GENERAL_FOUNDATION: 5 } },
    ],
  },

  {
    id: "q3",
    text: "How would you describe your experience with supplements?",
    helper: "Pick one.",
    multiSelect: false,
    maxSelect: 1,
    options: [
      { id: "1", label: "I'm new. I don't know where to start.", pointsByPath: { GENERAL_FOUNDATION: 5 } },
      { id: "2", label: "I've tried a few things with mixed results.", pointsByPath: {} },
      { id: "3", label: "I take supplements regularly and want to optimize.", pointsByPath: {} },
      { id: "4", label: "I'm data-driven and want something personalized.", pointsByPath: {} },
      { id: "5", label: "I'm skeptical but open-minded.", pointsByPath: { GENERAL_FOUNDATION: 5 } },
    ],
  },
  {
    id: "q4",
    text: "Which best describes your daily reality?",
    helper: "Pick one.",
    multiSelect: false,
    maxSelect: 1,
    options: [
      { id: "1", label: "Busy and stressed. Convenience matters.", pointsByPath: { ENERGY: 2, SLEEP_STRESS: 2 } },
      { id: "2", label: "Active lifestyle. Performance and recovery matter.", pointsByPath: { ENERGY: 2, LONGEVITY_CELLULAR: 2 } },
      { id: "3", label: "Inconsistent schedule. Sleep and energy are issues.", pointsByPath: { ENERGY: 2, SLEEP_STRESS: 2 } },
      { id: "4", label: "Family-focused. I need simple and sustainable.", pointsByPath: { GENERAL_FOUNDATION: 2 } },
      { id: "5", label: "Structured and disciplined. I like routines.", pointsByPath: { ENERGY: 2, LONGEVITY_CELLULAR: 2 } },
    ],
  },
  {
    id: "q5",
    text: "What feels reasonable to get started?",
    helper: "Pick one.",
    multiSelect: false,
    maxSelect: 1,
    options: [
      { id: "1", label: "I want to start simple and see how I feel.", pointsByPath: { GENERAL_FOUNDATION: 5 } },
      { id: "2", label: "I'm open to a focused solution if it makes sense.", pointsByPath: {} },
      { id: "3", label: "I want the best option for my goals.", pointsByPath: {} },
      { id: "4", label: "I'm not sure yet. Show me my options.", pointsByPath: { GENERAL_FOUNDATION: 5 } },
    ],
  },
];
// Solution path write-ups/blurbs for the final review page
const SOLUTION_PATH_BLURBS: Record<PathKey, string> = {
  ENERGY: "Feeling like low energy is quietly running the show? What we heard is that you don't need more caffeine—you need support that helps you feel steady, focused, and like yourself again, without the crash.",
  SLEEP_STRESS: "Are restless nights and constant stress part of your everyday life? What we heard is that you're not just tired—you're worn down. These products were chosen to help your body unwind and make sleep feel truly restorative again.",
  METABOLISM_WEIGHT: "It sounds like weight and metabolism are top of mind right now. What we heard is that you want progress that feels healthy and sustainable. These products support your body as it changes, helping you feel stronger, supported, and confident you're doing this the right way.",
  LONGEVITY_CELLULAR: "It sounds like you're thinking about how you age, not just how you feel today. What we heard is that you want to stay strong, capable, and resilient for longer. These products support cellular health so your body can age better—inside and out.",
  BRAIN_COGNITION: "Does mental clarity and sharp thinking matter to you? What we heard is that you rely on your mind every day. These products were chosen to support focus, memory, and mental sharpness so your brain keeps up with everything you ask of it.",
  GUT_DIGESTION: "It sounds like your gut has been a source of ongoing frustration. What we heard is that comfort, digestion, and feeling normal again matter to you. These products support gut health so your body can absorb nutrients better—and you can feel good day to day.",
  IMMUNE: "It sounds like staying healthy and resilient is important to you. What we heard is that you want your body to bounce back quickly and stay strong. These products were chosen to support immune health so life doesn't keep getting interrupted.",
  PERFORMANCE_RECOVERY: "Do you push your body and expect it to respond? What we heard is that performance and recovery both matter to you. These products were chosen to support energy, recovery, and resilience so your body keeps up with everything you demand of it.",
  GENERAL_FOUNDATION: "It sounds like you're looking for a solid starting point without overcomplicating things. What we heard is that you want to cover the basics well. These products were chosen to support your foundation so everything else you do works better.",
  PREGNANCY_INFERTILITY: "Are you thinking about what's best for both you and your baby right now? What we heard is that safety and proper nourishment matter most. These products were chosen to support you and your baby during this important time.",
};

const SOLUTION_PATHS: Record<PathKey, SolutionPath> = {
  ENERGY: {
    key: "ENERGY",
    title: "Energy",
    description: "Sustained energy, clarity, and daily drive.",
    products: [
      { name: "Energy", url: "/shop/product/03-1022", tagline: "Smooth, sustained energy.", imageSrc: PRODUCT_IMAGES["Energy"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular energy + healthy aging.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Essentials Pack", url: "/shop/product/05-0018", tagline: "Daily foundational support.", imageSrc: PRODUCT_IMAGES["Essentials Pack"] },
    ],
  },
  SLEEP_STRESS: {
    key: "SLEEP_STRESS",
    title: "Sleep + Stress",
    description: "Calmer nights, better recovery, and resilience.",
    products: [
      { name: "Sleep", url: "/shop/product/06-0003", tagline: "Sleep support formula.", imageSrc: PRODUCT_IMAGES["Sleep"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular baseline support.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Stress Support Pack", url: "/shop/product/05-0017", tagline: "Stress relief support.", imageSrc: PRODUCT_IMAGES["Stress Support Pack"] },
    ],
  },
  METABOLISM_WEIGHT: {
    key: "METABOLISM_WEIGHT",
    title: "Metabolism + Weight",
    description: "Supports cravings, metabolism, and consistency.",
    products: [
      { name: "Slim+", url: "/shop/product/15-1030", tagline: "Metabolic support.", imageSrc: PRODUCT_IMAGES["Slim+"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular energy support.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Essentials Pack", url: "/shop/product/05-0018", tagline: "Daily foundation.", imageSrc: PRODUCT_IMAGES["Essentials Pack"] },
    ],
  },
  LONGEVITY_CELLULAR: {
    key: "LONGEVITY_CELLULAR",
    title: "Longevity + Cellular",
    description: "Healthy aging, resilience, and cellular performance.",
    products: [
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "NAD+ support + cellular energy.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Load", url: "/shop/product/15-0200", tagline: "Performance support.", imageSrc: PRODUCT_IMAGES["Load"] },
      { name: "Essentials Pack", url: "/shop/product/05-0018", tagline: "Baseline stack.", imageSrc: PRODUCT_IMAGES["Essentials Pack"] },
    ],
  },
  BRAIN_COGNITION: {
    key: "BRAIN_COGNITION",
    title: "Brain + Cognition",
    description: "Clarity, focus, and mental performance support.",
    products: [
      { name: "Brain Support Pack", url: "/shop/product/05-0011", tagline: "Cognition stack.", imageSrc: PRODUCT_IMAGES["Brain Support Pack"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular support.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Energy", url: "/shop/product/03-1022", tagline: "Daily drive.", imageSrc: PRODUCT_IMAGES["Energy"] },
    ],
  },
  GUT_DIGESTION: {
    key: "GUT_DIGESTION",
    title: "Gut + Digestion",
    description: "Digestion, comfort, and gut support.",
    products: [
      { name: "Gut Support Pack", url: "/shop/product/05-0013", tagline: "Gut-focused stack.", imageSrc: PRODUCT_IMAGES["Gut Support Pack"] },
      { name: "Collagen+", url: "/shop/product/15-0104", tagline: "Structural support.", imageSrc: PRODUCT_IMAGES["Collagen+"] },
      { name: "Slim+", url: "/shop/product/15-1030", tagline: "Metabolic support.", imageSrc: PRODUCT_IMAGES["Slim+"] },
    ],
  },
  IMMUNE: {
    key: "IMMUNE",
    title: "Immune",
    description: "Everyday immune defense and resilience.",
    products: [
      { name: "Immune Support Pack", url: "/shop/product/05-0012", tagline: "Immune defense stack.", imageSrc: PRODUCT_IMAGES["Immune Support Pack"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular baseline.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Slim+", url: "/shop/product/15-1030", tagline: "Metabolic support.", imageSrc: PRODUCT_IMAGES["Slim+"] },
    ],
  },
  PERFORMANCE_RECOVERY: {
    key: "PERFORMANCE_RECOVERY",
    title: "Performance + Recovery",
    description: "Recover faster, feel better, perform stronger.",
    products: [
      { name: "Load", url: "/shop/product/15-0200", tagline: "Performance fuel.", imageSrc: PRODUCT_IMAGES["Load"] },
      { name: "Energy", url: "/shop/product/03-1022", tagline: "Sustained output.", imageSrc: PRODUCT_IMAGES["Energy"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular support.", imageSrc: PRODUCT_IMAGES["BioAge"] },
    ],
  },
  GENERAL_FOUNDATION: {
    key: "GENERAL_FOUNDATION",
    title: "General Foundation",
    description: "Clean baseline you can build on.",
    products: [
      { name: "Energy", url: "/shop/product/03-1022", tagline: "Daily drive.", imageSrc: PRODUCT_IMAGES["Energy"] },
      { name: "BioAge", url: "/shop/product/06-0015", tagline: "Cellular baseline.", imageSrc: PRODUCT_IMAGES["BioAge"] },
      { name: "Essentials Pack", url: "/shop/product/05-0018", tagline: "Daily foundation.", imageSrc: PRODUCT_IMAGES["Essentials Pack"] },
    ],
  },
  PREGNANCY_INFERTILITY: {
    key: "PREGNANCY_INFERTILITY",
    title: "Pregnancy + Nursing",
    description: "Support for you and your baby during this important time.",
    products: [
      { name: "Prenatal Pack", url: "/shop/product/05-0021", tagline: "Complete prenatal support.", imageSrc: PRODUCT_IMAGES["Prenatal Pack"] },
      { name: "Hydrate", url: "/shop/product/15-1042", tagline: "Essential hydration.", imageSrc: PRODUCT_IMAGES["Hydrate"] },
      { name: "DNA", url: "/shop/product/05-0033", tagline: "Cellular foundation.", imageSrc: PRODUCT_IMAGES["DNA"] },
    ],
  },
};

function formatIsoLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function formatDayOfWeek(date: Date): string {
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

function formatSessionLength(startMs: number, endMs: number): string {
  const diffMs = Math.max(0, endMs - startMs);
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
}

function withBlanks(values: string[], size: number): string[] {
  return Array.from({ length: size }, (_, idx) => values[idx] ?? "Blank");
}

function readTrackingQueue(): StartSmartSessionPayload[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TRACKING_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as StartSmartSessionPayload[];
  } catch {
    return [];
  }
}

function writeTrackingQueue(queue: StartSmartSessionPayload[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TRACKING_QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // no-op
  }
}

async function postSessionToApi(payload: StartSmartSessionPayload): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(STARTSMART_API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": STARTSMART_API_KEY,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function flushTrackingQueue(): Promise<void> {
  const queue = readTrackingQueue();
  if (queue.length === 0) return;

  const remaining: StartSmartSessionPayload[] = [];
  for (const payload of queue) {
    const ok = await postSessionToApi(payload);
    if (!ok) {
      remaining.push(payload, ...queue.slice(queue.indexOf(payload) + 1));
      break;
    }
  }

  writeTrackingQueue(remaining);
}

async function fetchIpAndLocation(): Promise<IpLocation> {
  const fallback: IpLocation = { ipAddress: "Unknown", location: "Unknown" };

  try {
    const ipResp = await fetch("https://api.ipify.org?format=json");
    if (ipResp.ok) {
      const ipData = (await ipResp.json()) as { ip?: string };
      if (ipData.ip) fallback.ipAddress = ipData.ip;
    }
  } catch {
    // no-op
  }

  try {
    const geoResp = await fetch("https://ipapi.co/json/");
    if (geoResp.ok) {
      const geo = (await geoResp.json()) as {
        ip?: string;
        city?: string;
        region?: string;
        country_name?: string;
      };
      const cityRegion = [geo.city, geo.region].filter(Boolean).join(", ");
      fallback.location = cityRegion || geo.country_name || "Unknown";
      if (geo.ip) fallback.ipAddress = geo.ip;
      return fallback;
    }
  } catch {
    // no-op
  }

  try {
    const geoResp = await fetch("https://ipwho.is/");
    if (geoResp.ok) {
      const geo = (await geoResp.json()) as {
        ip?: string;
        city?: string;
        region?: string;
        country?: string;
      };
      const cityRegion = [geo.city, geo.region].filter(Boolean).join(", ");
      fallback.location = cityRegion || geo.country || fallback.location;
      if (geo.ip) fallback.ipAddress = geo.ip;
    }
  } catch {
    // no-op
  }

  return fallback;
}



function computeScores(answers: AnswersState): Scores {
  const scores: Scores = {
    ENERGY: 0,
    SLEEP_STRESS: 0,
    METABOLISM_WEIGHT: 0,
    LONGEVITY_CELLULAR: 0,
    BRAIN_COGNITION: 0,
    GUT_DIGESTION: 0,
    IMMUNE: 0,
    PERFORMANCE_RECOVERY: 0,
    GENERAL_FOUNDATION: 0,
    PREGNANCY_INFERTILITY: 0,
  };

  for (const q of QUESTIONS) {
    const chosen = answers[q.id] ?? [];
    for (const optId of chosen) {
      const opt = q.options.find((o) => o.id === optId);
      if (!opt) continue;
      for (const [k, v] of Object.entries(opt.pointsByPath)) {
        scores[k as PathKey] += v ?? 0;
      }
    }
  }
  return scores;
}

function topNPaths(scores: Scores, n = 3): PathKey[] {
  const order = Object.keys(SOLUTION_PATHS) as PathKey[];
  return (Object.entries(scores) as [PathKey, number][])
    .sort((a, b) => (b[1] !== a[1] ? b[1] - a[1] : order.indexOf(a[0]) - order.indexOf(b[0])))
    .slice(0, n)
    .map(([k]) => k);
}
function PrimaryButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & { glow?: boolean }
) {
  const { className, glow, children, ...rest } = props;

  return (
    <button
      {...rest}
      className={clsx(
        "group relative inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold transition",
        "focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] disabled:opacity-40 disabled:cursor-not-allowed",
        "active:translate-y-[0.5px]",
        glow &&
          "shadow-[0_0_0_1px_rgba(255,255,255,0.10),0_16px_50px_-18px_rgba(0,162,255,0.75)]",
        className
      )}
      style={{
        background: "linear-gradient(90deg, rgba(0,162,255,1), rgba(46,49,146,1))",
      }}
    >
      {children}
    </button>
  );
}


function SecondaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className, ...rest } = props;
  return (
    <button
      {...rest}
      className={clsx(
        "inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold transition",
        "border border-[color:var(--border-1)] bg-[color:var(--surface-1)] hover:bg-[color:var(--surface-2)]",
        "focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] disabled:opacity-40 disabled:cursor-not-allowed",
        "active:translate-y-[0.5px]",
        className
      )}
    />
  );
}


function OptionCard({
  title,
  selected,
  disabled,
  hint,
  onClick,
}: {
  title: string;
  selected: boolean;
  disabled?: boolean;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "w-full text-left sm:text-center rounded-2xl border-[3px] px-5 py-4 sm:py-5 transition-all duration-150",
        "bg-[color:var(--surface-1)] hover:bg-[color:var(--surface-2)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
        "active:translate-y-[0.5px]",
        selected
          ? "bg-[color:var(--select-bg)] border-[color:var(--select-border)] shadow-[0_18px_55px_-30px_var(--select-glow)]"
          : "border-[color:var(--border-1)] hover:border-[color:var(--border-2)] shadow-none",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className="flex items-center justify-between sm:justify-center gap-3">
        <div>
          <div className="text-base sm:text-[17px] font-semibold leading-snug">{title}</div>
          {hint ? (
            <div className="mt-1.5 text-sm text-black dark:text-white">
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={clsx(
            "mt-0.5 h-5 w-5 rounded-full border sm:hidden",
            selected
              ? "border-[color:var(--radio-selected)]"
              : "border-[color:var(--radio)]"
          )}
          style={
            selected
              ? {
                  background:
                    "linear-gradient(90deg, rgba(0,162,255,1), rgba(46,49,146,1))",
                }
              : undefined
          }
        />
      </div>
    </button>
  );
}



export default function StartSmartWizard({ compact: _compact = false }: { compact?: boolean }) {
  const totalSteps = QUESTIONS.length;

  // Safari gets janky with backdrop-filter + frequent rerenders/unmounts.
  // Keep logic identical; only reduce expensive visual effects on Safari.
    const isSafari = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(ua);
  }, []);

  // Mobile detection for responsive inline styles (Exigo-safe)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Safari performance switch (visual-only)
  const reduceFx = isSafari;

  // Memoize axis line height to prevent recalculating on every render (causes memory churn)
  const axisLineHeight = useMemo(() => {
    if (typeof window === 'undefined') return 400;
    return Math.sqrt(Math.pow(window.innerWidth * 0.4, 2) + Math.pow(window.innerHeight * 0.3, 2));
  }, []); // Empty deps = calculate once on mount

  // Stage state - declared early so animation effects can use it
  const [stage, setStage] = useState<"start" | "quiz" | "results">("start");

  // PNG sequence animation for ALL browsers (memory-safe approach)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [sequenceFrames, setSequenceFrames] = useState<HTMLImageElement[]>([]);
  const sequenceFramesRef = useRef<HTMLImageElement[]>([]); // Ref for animation loop to access latest frames
  const [sequenceLoaded, setSequenceLoaded] = useState(false);
  const frameIndexRef = useRef(0);
  const animationRef = useRef<number>(0);
  const [sequenceCycle, setSequenceCycle] = useState(0); // Increment to trigger reload
  
  // Keep ref in sync with state for animation loop
  useEffect(() => {
    sequenceFramesRef.current = sequenceFrames;
  }, [sequenceFrames]);

  // Load PNG sequence from zip for ALL browsers - starts animation at 40% loaded
  // Uses memory cycling to prevent crashes
  useEffect(() => {
    let cancelled = false;
    
    const loadSequence = async () => {
      try {
        const response = await fetch(animSequenceZip);
        const blob = await response.blob();
        const zip = await JSZip.loadAsync(blob);
        
        let pngFiles = Object.keys(zip.files)
          .filter(name => name.endsWith('.png') && !name.startsWith('__MACOSX'))
          .sort();
        
        // On mobile/tablet (< 1024px), skip every other frame to halve memory usage
        const isMobileOrTablet = window.innerWidth < 1024;
        if (isMobileOrTablet) {
          pngFiles = pngFiles.filter((_, index) => index % 2 === 0);
          console.log('Animation: mobile/tablet mode - loading every other frame to save memory');
        }
        
        const totalFrames = pngFiles.length;
        const startThreshold = Math.floor(totalFrames * 0.4); // 40% threshold
        const frames: HTMLImageElement[] = [];
        const batchSize = 100;
        let hasStartedAnimation = false;
        
        for (let i = 0; i < pngFiles.length; i += batchSize) {
          if (cancelled) return;
          
          const batch = pngFiles.slice(i, i + batchSize);
          const batchPromises = batch.map(async (filename) => {
            const file = zip.files[filename];
            const blob = await file.async('blob');
            const url = URL.createObjectURL(blob);
            
            return new Promise<HTMLImageElement>((resolve) => {
              const img = new Image();
              img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
              img.onerror = () => { URL.revokeObjectURL(url); resolve(img); };
              img.src = url;
            });
          });
          
          const batchFrames = await Promise.all(batchPromises);
          frames.push(...batchFrames);
          
          // Start animation early at 40% loaded - update state progressively
          if (!cancelled && !hasStartedAnimation && frames.length >= startThreshold) {
            hasStartedAnimation = true;
            console.log(`Safari animation: starting early at ${frames.length}/${totalFrames} frames (${Math.round(frames.length/totalFrames*100)}%)`);
            setSequenceFrames([...frames]); // Copy current frames
            setSequenceLoaded(true);
          } else if (!cancelled && hasStartedAnimation) {
            // Continue updating frames array as more load (animation will pick them up)
            setSequenceFrames([...frames]);
          }
        }
        
        // Final update when all frames are loaded
        if (!cancelled && frames.length > 0) {
          console.log('Safari animation: fully loaded', frames.length, 'frames');
          setSequenceFrames([...frames]);
          if (!hasStartedAnimation) {
            // Fallback: if we never hit 40% threshold (small animation), start now
            setSequenceLoaded(true);
          }
        }
      } catch (err) {
        console.error('Failed to load animation sequence:', err);
      }
    };
    
    loadSequence();
    return () => { cancelled = true; };
  }, [sequenceCycle]); // Re-run when sequenceCycle changes to reload frames

  // Animate the PNG sequence on canvas for ALL browsers - ONLY on start page
  // After one full playthrough, clears memory and reloads to prevent crashes
  useEffect(() => {
    // Only animate when on start page and sequence is loaded
    if (!sequenceLoaded || sequenceFramesRef.current.length === 0 || stage !== "start") {
      // Cancel any running animation when leaving start page
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      // Clear canvas to free GPU memory when not on start page
      if (stage !== "start" && canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const firstFrame = sequenceFramesRef.current[0];
    canvas.width = firstFrame.naturalWidth;
    canvas.height = firstFrame.naturalHeight;
    
    const fps = 30;
    const frameDuration = 1000 / fps;
    let lastTime = 0;
    let cycleComplete = false;
    
    const animate = (time: number) => {
      const frames = sequenceFramesRef.current;
      if (cycleComplete || frames.length === 0) return;
      
      if (time - lastTime >= frameDuration) {
        lastTime = time;
        
        const frame = frames[frameIndexRef.current];
        if (frame && frame.complete && frame.naturalWidth > 0) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(frame, 0, 0);
        }
        
        frameIndexRef.current++;
        
        // Check if we've completed one full cycle
        if (frameIndexRef.current >= frames.length) {
          cycleComplete = true;
          frameIndexRef.current = 0;
          
          // Clear memory: remove all frame references
          console.log('Safari animation: cycle complete, clearing memory');
          setSequenceFrames([]);
          sequenceFramesRef.current = [];
          setSequenceLoaded(false);
          
          // After a brief pause showing placeholder, reload the sequence
          setTimeout(() => {
            if (stage === "start") {
              console.log('Safari animation: reloading sequence');
              setSequenceCycle(c => c + 1);
            }
          }, 500); // Show placeholder for 500ms
          
          return;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => { 
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
    };
  }, [sequenceLoaded, stage]); // Removed sequenceFrames - use ref instead to avoid restarts

  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<AnswersState>({});
  const answersRef = useRef<AnswersState>({});

  const sessionStartAtRef = useRef<number | null>(null);
  const resultsReachedAtRef = useRef<number | null>(null);
  const isSubmittingSessionRef = useRef(false);
  const ipLocationRef = useRef<IpLocation>({ ipAddress: "Unknown", location: "Unknown" });
  const ipLookupPromiseRef = useRef<Promise<IpLocation> | null>(null);
  const futureTrackingRef = useRef<FutureTrackingState>({
    restartCount: 0,
    backEvents: [],
    viewedRecommendedProducts: {
      product1: false,
      product2: false,
      product3: false,
    },
  });

  // Dark mode detection
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Safari: prevent "jump/lag" by decoding images before they first paint.
  useEffect(() => {
    const sources = [
      heroLight,
      heroDark,
      ...Object.values(PRODUCT_IMAGES),
    ];

    let cancelled = false;

    (async () => {
      for (const src of sources) {
        try {
          const img = new Image();
          img.src = src;

          // Force decode early (Safari benefits a lot from this)
          if (typeof (img as any).decode === "function") {
            await (img as any).decode();
          }
        } catch {
          // ignore decode failures
        }
        if (cancelled) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);


  const question = QUESTIONS[stepIdx];
  const selected = answers[question?.id ?? ""] ?? [];

  const canProceed = useMemo(() => {
    if (stage !== "quiz") return true;
    if (!question) return false;
    if (question.multiSelect) return selected.length >= 1 && selected.length <= question.maxSelect;
    return selected.length === 1;
  }, [question, selected, stage]);

  const scores = useMemo(() => computeScores(answers), [answers]);
const topPathKey = useMemo(() => topNPaths(scores, 1)[0], [scores]);
const topPath = useMemo(() => SOLUTION_PATHS[topPathKey], [topPathKey]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);



  const persistFutureTrackingDraft = () => {
    try {
      window.localStorage.setItem(
        FUTURE_TRACKING_DRAFT_STORAGE_KEY,
        JSON.stringify(futureTrackingRef.current)
      );
    } catch {
      // no-op
    }
  };

  const primeIpLocation = () => {
    if (!ipLookupPromiseRef.current) {
      ipLookupPromiseRef.current = fetchIpAndLocation().then((result) => {
        ipLocationRef.current = result;
        return result;
      });
    }
    return ipLookupPromiseRef.current;
  };

  const buildTrackingPayload = async (): Promise<StartSmartSessionPayload> => {
    const startMs = sessionStartAtRef.current ?? Date.now();
    const reachedMs = resultsReachedAtRef.current ?? Date.now();
    const startDate = new Date(startMs);

    const q1 = withBlanks(answersRef.current.q1 ?? [], 2);
    const q2 = withBlanks(answersRef.current.q2 ?? [], 7);

    const ipLocation = ipLookupPromiseRef.current
      ? await ipLookupPromiseRef.current
      : ipLocationRef.current;

    const mappedCategory = OUTPUT_CATEGORY_MAP[topPath.key] ?? topPath.key;

    return {
      ipAddress: ipLocation.ipAddress,
      location: ipLocation.location,
      startDate: formatIsoLocal(startDate),
      dayOfWeek: formatDayOfWeek(startDate),
      sessionLength: formatSessionLength(startMs, reachedMs),
      question1_1: q1[0],
      question1_2: q1[1],
      question2_1: q2[0],
      question2_2: q2[1],
      question2_3: q2[2],
      question2_4: q2[3],
      question2_5: q2[4],
      question2_6: q2[5],
      question2_7: q2[6],
      question3: answersRef.current.q3?.[0] ?? "Blank",
      question4: answersRef.current.q4?.[0] ?? "Blank",
      question5: answersRef.current.q5?.[0] ?? "Blank",
      outputCategory: mappedCategory,
      outputProduct1: topPath.products[0]?.name ?? "Blank",
      outputProduct2: topPath.products[1]?.name ?? "Blank",
      outputProduct3: topPath.products[2]?.name ?? "Blank",
    };
  };

  const queueAndFlushTrackingPayload = async (payload: StartSmartSessionPayload) => {
    const queue = readTrackingQueue();
    const payloadSig = JSON.stringify(payload);
    const alreadyQueued = queue.some((entry) => JSON.stringify(entry) === payloadSig);
    if (!alreadyQueued) {
      queue.push(payload);
      writeTrackingQueue(queue);
    }
    await flushTrackingQueue();
  };

  const submitSessionTracking = async () => {
    if (isSubmittingSessionRef.current) return;

    isSubmittingSessionRef.current = true;
    try {
      const payload = await buildTrackingPayload();
      await queueAndFlushTrackingPayload(payload);
    } finally {
      isSubmittingSessionRef.current = false;
    }
  };

  const sendTrackingNow = () => {
    resultsReachedAtRef.current = Date.now();
    void submitSessionTracking();
  };

  const markProductCardViewed = (productIndex: 1 | 2 | 3) => {
    if (productIndex === 1) futureTrackingRef.current.viewedRecommendedProducts.product1 = true;
    if (productIndex === 2) futureTrackingRef.current.viewedRecommendedProducts.product2 = true;
    if (productIndex === 3) futureTrackingRef.current.viewedRecommendedProducts.product3 = true;
    persistFutureTrackingDraft();
  };

  useEffect(() => {
    void flushTrackingQueue();
    const onOnline = () => {
      void flushTrackingQueue();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);




    function resetAll() {
    // Restart = new session
    futureTrackingRef.current.restartCount += 1;
    persistFutureTrackingDraft();

    // Reset session tracking for new session
    sessionStartAtRef.current = null;
    resultsReachedAtRef.current = null;
    futureTrackingRef.current = {
      restartCount: 0,
      backEvents: [],
      viewedRecommendedProducts: {
        product1: false,
        product2: false,
        product3: false,
      },
    };
    persistFutureTrackingDraft();

    // Reset animation state for ALL browsers BEFORE changing stage
    // This ensures placeholder shows first, then animation fades in
    // Cancel current animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = 0;
    }
    // Reset frame index to beginning
    frameIndexRef.current = 0;
    // Hide the canvas, show placeholder
    setSequenceLoaded(false);
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
    
    // Reset quiz state
    setStage("start");
    setStepIdx(0);
    setAnswers({});
    
    // After state updates and brief delay, trigger animation reload
    // This ensures the placeholder shows, then animation fades in
    setTimeout(() => {
      if (sequenceFramesRef.current.length > 0) {
        // We still have frames in memory, reset index and restart
        frameIndexRef.current = 0;
        setSequenceLoaded(true);
      } else {
        // Frames were cleared, trigger a reload cycle
        setSequenceCycle(c => c + 1);
      }
    }, 300); // Longer delay to ensure placeholder is visible
  }

 


      function startQuiz() {
    // Start new session if not already started
    if (!sessionStartAtRef.current) {
      sessionStartAtRef.current = Date.now();
      void primeIpLocation();
    }
    setStage("quiz");
    setStepIdx(0);
  }


      function goNext() {
    if (!canProceed) return;

    if (stepIdx < totalSteps - 1) {
      setStepIdx((i) => i + 1);
      return;
    }

    // Reached final results page - send tracking immediately
    sendTrackingNow();
    setStage("results");
  }


  function goBack() {
    futureTrackingRef.current.backEvents.push({
      stage,
      stepIdx,
      at: new Date().toISOString(),
    });
    persistFutureTrackingDraft();
    if (stage === "results") {
      // Going back from results - will send again if they return
      setStage("quiz");
      setStepIdx(totalSteps - 1);
      return;
    }
    if (stepIdx > 0) {
      setStepIdx((i) => i - 1);
    } else {
      // Going back to start page - trigger animation reset for ALL browsers
      // Cancel current animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      // Reset frame index to beginning
      frameIndexRef.current = 0;
      // Hide the canvas, show placeholder
      setSequenceLoaded(false);
      
      // Clear canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      
      setStage("start");
      
      // After brief delay, restart the animation
      setTimeout(() => {
        if (sequenceFramesRef.current.length > 0) {
          // We still have frames in memory, reset index and restart
          frameIndexRef.current = 0;
          setSequenceLoaded(true);
        } else {
          // Frames were cleared, trigger a reload cycle
          setSequenceCycle(c => c + 1);
        }
      }, 300); // Longer delay to ensure placeholder is visible
    }
  }

    function toggleOption(optId: string) {
    if (!question) return;
    if (!question.options.some((o) => o.id === optId)) return;

    setAnswers((prev) => {
      const current = prev[question.id] ?? [];

      // Q2 special rule:
      // - Selecting "None — I want a solid foundation" clears all other selections
      // - Selecting any other option clears "None" if it was selected
      if (question.id === "q2") {
        const NONE_ID = "8";

        if (optId === NONE_ID) {
          return { ...prev, [question.id]: [NONE_ID] };
        }

        const withoutNone = current.filter((x) => x !== NONE_ID);
        const exists = withoutNone.includes(optId);
        const next = exists ? withoutNone.filter((x) => x !== optId) : [...withoutNone, optId];

        return { ...prev, [question.id]: next };
      }

      // Default behavior (unchanged) for all other questions
      if (question.multiSelect) {
        const exists = current.includes(optId);
        let next = exists ? current.filter((x) => x !== optId) : [...current, optId];
        next = next.slice(0, question.maxSelect);
        return { ...prev, [question.id]: next };
      }

      return { ...prev, [question.id]: [optId] };
    });
  }


    const bgStyle: React.CSSProperties = useMemo(() => {
    // Base background gradient using CSS variables
    const baseGradient = "linear-gradient(135deg, var(--bg-gradient-start) 0%, var(--bg-gradient-mid) 50%, var(--bg-gradient-end) 100%)";
    
    if (reduceFx) {
      // Safari: simpler overlay gradient ON TOP of base background
      return {
        background: `linear-gradient(180deg, rgba(0,162,255,0.10) 0%, rgba(46,49,146,0.12) 55%, transparent 100%), ${baseGradient}`,
      };
    }

    return {
      background:
        "radial-gradient(1200px 800px at 20% 15%, rgba(0,162,255,0.18), transparent 55%)," +
        "radial-gradient(900px 700px at 80% 35%, rgba(46,49,146,0.22), transparent 60%)," +
        `radial-gradient(900px 600px at 40% 95%, rgba(255,255,255,0.06), transparent 60%), ${baseGradient}`,
    };
  }, [reduceFx]);


   return (
    <div
      className={clsx(
        "w-full",
        "idlife-bg text-[var(--app-fg)]"
      )}
      style={bgStyle}
    >
      {/* Full-width widget - no outer padding/margins */}
      <div className="relative w-full">

                                <div 
                  className="relative isolate border-0 bg-[color:var(--surface-1)] overflow-hidden"
                  style={{
                    /* Use 100svh (small viewport) to account for mobile browser chrome AND external headers */
                    /* Subtract ~120px for IDLife nav header + shipping banner on mobile */
                    /* Only apply minHeight for start screen - quiz/results should fit content */
                    minHeight: stage === 'start' ? 'calc(100svh - 120px)' : 'auto',
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Atmospheric layer - simplified for Safari performance */}
                  {!reduceFx ? (
                  <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
                    {/* LIGHT MODE: airy sky / soft glow */}
                    <div
                      className="absolute inset-0 opacity-100 dark:opacity-0"
                      style={{
                        backgroundImage:
                          "radial-gradient(900px 520px at 20% 18%, rgba(0,162,255,0.20), transparent 60%)," +
                          "radial-gradient(760px 520px at 70% 28%, rgba(46,49,146,0.14), transparent 62%)," +
                          "radial-gradient(900px 650px at 55% 85%, rgba(255,255,255,0.78), transparent 62%)," +
                          "linear-gradient(180deg, rgba(245,248,255,0.95) 0%, rgba(232,238,252,0.92) 42%, rgba(226,232,248,0.90) 100%)",
                        filter: "saturate(1.05) contrast(1.02)",
                      }}
                    />

                    {/* DARK MODE: deep space / quiet nebula */}
                    <div
                      className="absolute inset-0 opacity-0 dark:opacity-100"
                      style={{
                        backgroundImage:
                          "radial-gradient(1100px 720px at 18% 22%, rgba(0,162,255,0.18), transparent 58%)," +
                          "radial-gradient(900px 680px at 78% 34%, rgba(46,49,146,0.20), transparent 62%)," +
                          "linear-gradient(180deg, rgba(9,12,22,0.96) 0%, rgba(10,14,26,0.94) 46%, rgba(8,11,20,0.96) 100%)",
                      }}
                    />

                    {/* Subtle “stars / grain” layer (kept tasteful) */}
                    <div
                      className="absolute inset-0 opacity-[0.06] dark:opacity-[0.10] mix-blend-overlay"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 12% 22%, rgba(255,255,255,0.55) 0.8px, transparent 1.6px)," +
                          "radial-gradient(circle at 72% 32%, rgba(255,255,255,0.45) 0.7px, transparent 1.6px)",
                        backgroundSize: "260px 260px",
                      }}
                    />

                    {/* Soft vignette to keep text/CTA readable */}
                    <div className="absolute inset-0 bg-[radial-gradient(1200px_700px_at_28%_30%,transparent_40%,rgba(0,0,0,0.15)_100%)] opacity-0 dark:opacity-100" />
                    <div className="absolute inset-0 bg-[radial-gradient(1200px_700px_at_28%_30%,transparent_40%,rgba(255,255,255,0.12)_100%)] opacity-100 dark:opacity-0" />
                  </div>
                  ) : (
                    /* Safari: simple gradient background for better performance */
                    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
                      <div
                        className="absolute inset-0"
                        style={{
                          background: "linear-gradient(180deg, rgba(0,162,255,0.08) 0%, rgba(46,49,146,0.06) 100%)",
                        }}
                      />
                    </div>
                  )}

                  <div 
                    className="relative z-10 p-4 sm:p-6 lg:p-8"
                    style={{
                      flex: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: stage === 'start' ? 'center' : 'flex-start'
                    }}
                  >

            <AnimatePresence mode="wait">
              {stage === "start" ? (
                <motion.div
                  key="start"
                  initial={{ opacity: 0, y: reduceFx ? 0 : 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceFx ? 0 : -12 }}
                  transition={{ duration: reduceFx ? 0.08 : 0.15, ease: reduceFx ? [0.25, 0.1, 0.25, 1] : "easeOut" }}
                  style={{ willChange: 'transform, opacity' }}
                >
                                    <div className="grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-16">
                                        <div className="lg:col-span-6 text-center" style={{ willChange: 'auto' }}>
                      <h1 
                        className="text-display mx-auto max-w-xl text-[2.5rem] sm:text-5xl md:text-[3.25rem] lg:text-6xl font-extrabold text-[var(--text-primary)] leading-[1.1]" 
                        style={{ 
                          fontFamily: "'Sora', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontWeight: 700,
                          lineHeight: 1.08,
                          letterSpacing: '-0.03em',
                          willChange: 'auto'
                        }}
                      >
                        Find your best <img 
                          src={idlifeLogo}
                          alt="IDLife"
                          style={{
                            display: 'inline-block',
                            height: '0.95em',
                            width: 'auto',
                            verticalAlign: 'middle',
                            marginTop: '-0.15em',
                            marginLeft: '0.1em',
                            marginRight: '0.02em'
                          }}
                        /> start — fast.
                      </h1>

                      <p 
                        className="text-body mt-4 sm:mt-6 mx-auto max-w-lg text-lg sm:text-xl lg:text-[1.375rem] leading-relaxed text-black dark:text-white"
                        style={{
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                          fontWeight: 400,
                          lineHeight: 1.6,
                          letterSpacing: '-0.011em',
                          color: 'inherit'
                        }}
                      >
                        StartSmart helps you discover the perfect supplements based on your unique <span style={{ whiteSpace: 'nowrap' }}>wellness goals.</span>
                      </p>

                      <div className="mt-6 sm:mt-10">
                        <style>{`
                          .ss-start-btn, .ss-start-btn * {
                            color: #FFFFFF !important;
                          }
                          .ss-start-btn svg {
                            stroke: #FFFFFF !important;
                          }
                        `}</style>
                        <button
                          onClick={startQuiz}
                          className="ss-start-btn group relative inline-flex items-center gap-3 px-8 py-4 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-[var(--idlife-blue)] to-[var(--idlife-blue-dark)] rounded-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.1),0_20px_50px_-15px_var(--btn-glow)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.15),0_25px_60px_-15px_var(--btn-glow)] transition-all duration-200 active:scale-[0.98] overflow-hidden"
                          style={{
                            background: 'linear-gradient(to right, #0084FF, #0066CC)',
                            color: '#FFFFFF'
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                          <span className="relative" style={{ color: '#FFFFFF' }}>StartSmart</span>
                          <span 
                            className="relative inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white/20 text-white transition-transform group-hover:translate-x-1"
                            style={{ color: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.2)' }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: '#FFFFFF' }}>
                              <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>


                                        {/* Premium Hero Visual with Diagonal Treatment */}
                    <div className="relative lg:col-span-6">
                      <div 
                        className="relative mx-auto mt-6 sm:mt-8 w-full max-w-md sm:max-w-lg lg:mt-0 aspect-square"
                        style={isMobile ? {
                          /* Mobile only: constrain height to fit viewport with IDLife header */
                          maxHeight: 'min(70vw, 45vh)',
                        } : {
                          /* Desktop: constrain to original smaller size */
                          maxWidth: '450px',
                        }}
                      >
                        {/* Diagonal gradient backdrop */}
                        <div className="absolute inset-0 -rotate-6 scale-105 rounded-[2.5rem] bg-gradient-to-br from-[var(--idlife-blue)]/20 to-[var(--idlife-purple)]/20 blur-lg"></div>
                        
                        {/* Main glass container with diagonal angle */}
                        <div className="absolute inset-0 rounded-[2.5rem] bg-[var(--surface-glass)] border border-[var(--border-2)] shadow-[0_40px_100px_-40px_rgba(0,0,0,0.4)] overflow-hidden">
                          
                          {/* Premium gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-[var(--idlife-blue)]/5 via-transparent to-[var(--idlife-purple)]/5"></div>
                          
                          {/* Diagonal shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50"></div>

                          {/* Hero video loop - zero glitching! */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            {/* Static glow - disabled on Safari for performance */}
                            {!reduceFx && (
                            <div 
                              className="absolute inset-0 rounded-3xl pointer-events-none"
                              style={{
                                background: isDarkMode 
                                  ? 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.25), rgba(59, 130, 246, 0.15), transparent 70%)'
                                  : 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.2), rgba(99, 102, 241, 0.12), transparent 70%)',
                                filter: 'blur(60px)',
                              }}
                            />
                            )}
                            
                            {/* Video container */}
                            <div className="absolute inset-0 flex items-center justify-center p-4">
                              {/* Border */}
                              <div
                                className="absolute inset-0 rounded-2xl pointer-events-none z-20 ss-video-border"
                              />
                              
                              {/* Canvas-based PNG sequence animation for ALL browsers */}
                              <div className="relative h-full w-full flex items-center justify-center overflow-hidden">
                                {/* Static image placeholder while loading/between cycles */}
                                <img
                                  src={isDarkMode ? heroDark : heroLight}
                                  alt="IDLife Products"
                                  className="h-full w-full object-contain"
                                  style={{ 
                                    opacity: sequenceLoaded ? 0 : 1,
                                    transition: 'opacity 0.5s ease-in-out',
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 10
                                  }}
                                />
                                <canvas
                                  ref={canvasRef}
                                  className="h-full w-full"
                                  style={{ 
                                    opacity: sequenceLoaded ? 1 : 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                    objectFit: 'contain',
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: sequenceLoaded ? 11 : 5
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Premium floating particles with depth */}
                          <motion.div 
                            className="absolute top-8 right-8 h-3 w-3 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg z-20"
                            animate={{
                              scale: [1, 1.4, 1],
                              opacity: [0.7, 1, 0.7],
                              y: [0, -8, 0],
                              boxShadow: [
                                '0 0 25px rgba(59, 130, 246, 0.6), 0 0 50px rgba(59, 130, 246, 0.3)',
                                '0 0 45px rgba(59, 130, 246, 0.9), 0 0 80px rgba(59, 130, 246, 0.5)',
                                '0 0 25px rgba(59, 130, 246, 0.6), 0 0 50px rgba(59, 130, 246, 0.3)',
                              ],
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                          <motion.div 
                            className="absolute top-12 right-16 h-1.5 w-1.5 rounded-full bg-gradient-to-br from-blue-300 to-blue-500 shadow-md z-20"
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0.5, 0.9, 0.5],
                              x: [0, -5, 0],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 0.5,
                            }}
                          />
                          <motion.div 
                            className="absolute bottom-12 left-8 h-2.5 w-2.5 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg z-20"
                            animate={{
                              scale: [1, 1.4, 1],
                              opacity: [0.7, 1, 0.7],
                              y: [0, 8, 0],
                              boxShadow: [
                                '0 0 25px rgba(147, 51, 234, 0.6), 0 0 50px rgba(147, 51, 234, 0.3)',
                                '0 0 45px rgba(147, 51, 234, 0.9), 0 0 80px rgba(147, 51, 234, 0.5)',
                                '0 0 25px rgba(147, 51, 234, 0.6), 0 0 50px rgba(147, 51, 234, 0.3)',
                              ],
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 1.2,
                            }}
                          />
                          <motion.div 
                            className="absolute bottom-16 left-16 h-1.5 w-1.5 rounded-full bg-gradient-to-br from-purple-300 to-purple-500 shadow-md z-20"
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0.5, 0.9, 0.5],
                              x: [0, 5, 0],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 1.8,
                            }}
                          />
                          {/* Additional ambient particles */}
                          <motion.div 
                            className="absolute top-1/3 left-12 h-1 w-1 rounded-full bg-gradient-to-br from-indigo-300 to-indigo-500 shadow-sm z-20"
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.3, 0.7, 0.3],
                              y: [0, -10, 0],
                            }}
                            transition={{
                              duration: 4,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 0.8,
                            }}
                          />
                          <motion.div 
                            className="absolute top-2/3 right-12 h-1 w-1 rounded-full bg-gradient-to-br from-cyan-300 to-cyan-500 shadow-sm z-20"
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.3, 0.7, 0.3],
                              x: [0, 10, 0],
                            }}
                            transition={{
                              duration: 4,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 2,
                            }}
                          />
                          
                          {/* Rotating axis line (subtle visual) */}
                          <motion.div 
                            className="absolute z-0 pointer-events-none"
                            style={{
                              left: 'calc(2rem + 4px)',
                              bottom: 'calc(3rem + 4px)',
                              width: '1px',
                              height: axisLineHeight,
                              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(59, 130, 246, 0.1))',
                              transformOrigin: 'top left',
                              transform: 'rotate(-35deg)',
                            }}
                            animate={{
                              opacity: [0.1, 0.3, 0.1],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          ></motion.div>
                        </div>
                      </div>
                    </div>

                  </div>



               

                  {/* removed feature cards */}


                </motion.div>
              ) : null}

              {stage === "quiz" && question ? (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: reduceFx ? 0 : 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceFx ? 0 : -12 }}
                  transition={{ duration: reduceFx ? 0.08 : 0.15, ease: reduceFx ? [0.25, 0.1, 0.25, 1] : "easeOut" }}
                >
                  <div className="flex flex-col gap-6">
                    <div className="w-full">
                                            <div className="text-xs text-black dark:text-white">
                        StartSmart
                      </div>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-[color:var(--surface-1)] overflow-hidden ring-1 ring-[color:var(--border-1)]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.round(((stepIdx + 1) / totalSteps) * 100)}%`,
                            background:
                              "linear-gradient(90deg, rgba(0,162,255,1), rgba(46,49,146,1))",
                          }}
                        />
                      </div>

                    </div>

                   {/* Quiz layout: centered heading above, 2-column answers below on desktop */}
                   <div className="space-y-6">
                     {/* Heading section - always centered on top */}
                     <div className="text-center">
                        <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-extrabold tracking-tight leading-snug">
                          {question.text}
                        </h2>

                                                {question.multiSelect ? (
  <div className="mt-4 space-y-4">
    <div className="flex items-center justify-center gap-3 text-sm sm:text-base">
      <span className="text-black dark:text-white">{question.helper}</span>
      {question.maxSelect < question.options.length ? (
        <span className="tabular-nums text-black dark:text-white">
          {selected.length}/{question.maxSelect} selected
        </span>
      ) : null}
    </div>
    <div className="flex items-center justify-center gap-4">
      <button
        onClick={goBack}
        className="text-base font-medium text-black dark:text-white hover:text-black dark:hover:text-white transition"
      >
        Back
      </button>
      <button
        onClick={goNext}
        disabled={!canProceed}
        className="rounded-lg bg-gradient-to-r from-sky-500 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md disabled:opacity-40"
      >
        {stepIdx === totalSteps - 1 ? "See results" : "Continue"}
      </button>
    </div>
  </div>
) : (
  <div className="mt-6 flex items-center justify-center gap-4">
    <button
      onClick={goBack}
      className="text-base font-medium text-black dark:text-white hover:text-black dark:hover:text-white transition"
    >
      Back
    </button>

    <button
      onClick={goNext}
      disabled={!canProceed}
      className="rounded-lg bg-gradient-to-r from-sky-500 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md disabled:opacity-40"
    >
      {stepIdx === totalSteps - 1 ? "See results" : "Continue"}
    </button>
  </div>
)}

                      </div>


                      {/* Answer options - 2 columns on desktop, center last odd item */}
                      {(() => {
                        const opts = question.options;
                        const isOdd = opts.length % 2 !== 0;
                        const pairedOpts = isOdd ? opts.slice(0, -1) : opts;
                        const lastOpt = isOdd ? opts[opts.length - 1] : null;

                        return (
                          <>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {pairedOpts.map((opt) => {
                                const isSelected = selected.includes(opt.id);
                                const atLimit =
                                  question.multiSelect &&
                                  !isSelected &&
                                  selected.length >= question.maxSelect;

                                return (
                                  <OptionCard
                                    key={opt.id}
                                    title={opt.label}
                                    selected={isSelected}
                                    disabled={atLimit}
                                    hint={opt.sublabel}
                                    onClick={() => toggleOption(opt.id)}
                                  />
                                );
                              })}
                            </div>
                            {lastOpt && (
                              <div className="mt-3 flex justify-center">
                                <div className="w-full sm:w-[calc(50%-6px)]">
                                  <OptionCard
                                    key={lastOpt.id}
                                    title={lastOpt.label}
                                    selected={selected.includes(lastOpt.id)}
                                    disabled={
                                      question.multiSelect &&
                                      !selected.includes(lastOpt.id) &&
                                      selected.length >= question.maxSelect
                                    }
                                    hint={lastOpt.sublabel}
                                    onClick={() => toggleOption(lastOpt.id)}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
              ) : null}

              {stage === "results" ? (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: reduceFx ? 0 : 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceFx ? 0 : -12 }}
                  transition={{ duration: reduceFx ? 0.08 : 0.15, ease: reduceFx ? [0.25, 0.1, 0.25, 1] : "easeOut" }}
                >
                  <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
                    <div className="text-center sm:text-left">
                     <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-extrabold tracking-tight leading-snug">
  Your recommended starting point
</h2>


                    </div>

                    <div className="flex gap-2">
                      <SecondaryButton onClick={goBack}>Back</SecondaryButton>
                      <PrimaryButton onClick={resetAll} glow className="text-white">
  Restart
</PrimaryButton>

                    </div>
                  </div>

                 <div className="mt-4 sm:mt-5 lg:mt-4 grid gap-4 sm:gap-3 lg:gap-2">
  <div className="rounded-3xl border border-transparent bg-transparent p-3 sm:p-4 lg:p-3">
    
    {/* Premium Blurb Section - Above Title */}
    <div className="mb-5 sm:mb-4 lg:mb-3">
      <div className="relative group">
        {/* Main card with glass morphism - premium dark mode styling */}
        <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10 dark:from-blue-500/20 dark:via-indigo-600/15 dark:to-purple-600/20 ${reduceFx ? '' : 'backdrop-blur-xl'} ring-1 ring-white/10 dark:ring-white/20`}>
          
          {/* Subtle glow effect for dark mode */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/0 via-transparent to-purple-400/0 dark:from-blue-400/10 dark:to-purple-500/10 opacity-60"></div>
          
          {/* Content */}
          <div className="relative px-5 sm:px-6 lg:px-7 py-4 sm:py-5 lg:py-4">
            {/* Icon and label - more prominent */}
            <div className="flex items-center justify-center sm:justify-start gap-3 mb-3 lg:mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25 dark:shadow-blue-500/40">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="text-sm font-bold uppercase tracking-wider bg-gradient-to-r from-blue-400 to-indigo-400 dark:from-blue-300 dark:to-indigo-300 bg-clip-text text-transparent">
                Personalized for you
              </div>
            </div>
            
            {/* Blurb text - larger on mobile */}
            <p className="text-[17px] sm:text-lg lg:text-base leading-relaxed lg:leading-normal text-gray-700 dark:text-white font-medium text-center sm:text-left">
              {SOLUTION_PATH_BLURBS[topPath.key]}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-3xl mx-auto sm:mx-0 text-center sm:text-left">
      <div className="text-xl sm:text-2xl font-extrabold tracking-tight">
        {topPath.title}
      </div>
           <div className="mt-2 text-base text-black dark:text-white">
        {topPath.description}
      </div>

    </div>

    <div className="mt-5 sm:mt-4 lg:mt-3 flex flex-wrap justify-center gap-5 sm:gap-4 lg:gap-3">
                  {topPath.products.map((p, idx) => (
  <div
    key={`${p.name}-${idx}`}
    className={clsx(
  "group relative overflow-hidden rounded-2xl bg-white/[0.04] p-4 sm:p-5 lg:p-4",
  "ring-1 ring-white/10 hover:ring-white/20",
  "transition-transform duration-200 ease-out",
  "hover:bg-white/[0.055]",
  "hover:-translate-y-[2px]",
  "w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.5rem)]"
)}
    style={{ willChange: 'transform' }}
  >
{idx === 0 && (
  <div className="absolute right-4 top-4 rounded-full bg-blue-500/90 px-3 py-1 text-xs font-semibold text-white shadow-md">
    #1 Recommended
  </div>
)}
    {/* Premium glow + lift (subtle, not cheesy) */}
       <div
      className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"
      style={{
        background:
          "radial-gradient(800px 320px at 25% 15%, rgba(0,162,255,0.14), transparent 55%)," +
          "radial-gradient(700px 280px at 85% 25%, rgba(46,49,146,0.14), transparent 60%)",
      }}
    />


    {/* Image area (bigger, fills space, fewer “boxes”) */}
    <a href={p.url ?? "#"} target="_blank" rel="noopener noreferrer" className="relative mx-auto w-full overflow-hidden rounded-2xl block cursor-pointer" onClick={() => markProductCardViewed((idx + 1) as 1 | 2 | 3)}>
      
      <div className="relative h-[280px] sm:h-[300px] md:h-[320px] lg:h-[240px]">
        {p.imageSrc ? (
          <img
            src={p.imageSrc}
            alt={p.name}
            loading="lazy"
            decoding="async"
            className={clsx(
  "absolute inset-0 mx-auto h-full w-full object-contain",
  "drop-shadow-[0_19px_45px_rgba(0,0,0,0.35)]",
  "transition-transform duration-200 ease-out",
  "scale-[1.22] sm:scale-[1.26] md:scale-[1.30]",
  "group-hover:scale-[1.28] sm:group-hover:scale-[1.32] md:group-hover:scale-[1.36]"
)}
            style={{ willChange: 'transform' }}
          />
        ) : (
          <div className="h-full w-full" />
        )}
      </div>
    </a>

    {/* Name + tagline */}
    <div className="mt-3 lg:mt-2 min-w-0 text-center sm:text-left">
      <div className="text-base lg:text-[15px] font-semibold leading-tight sm:text-lg lg:text-base">{p.name}</div>
      {p.tagline ? (
  <div className="mt-1.5 text-base text-black dark:text-white">
    {p.tagline}
  </div>
) : null}

    </div>

    {/* Actions */}
    <div className="mt-3 lg:mt-2 flex items-center justify-center sm:justify-start gap-2">
      <a
        href={p.url ?? "#"}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => markProductCardViewed((idx + 1) as 1 | 2 | 3)}
        className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 ease-out hover:brightness-110 active:translate-y-[1px]"

        style={{
          background: "linear-gradient(90deg, rgba(0,162,255,1), rgba(46,49,146,1))",
        }}
      >
        View details
      </a>
    </div>
  </div>
))}



    </div>
  </div>

	                </div>
	              </motion.div>
	            ) : null}
	            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
